<script lang="ts">
  /**
   * Your picture, your coins, frames and accessories, your badges and friends (brief §17.2,
   * amendments 21 and 23).
   *
   * The unearned badges are shown too, greyed out with what each one needs. A list of only
   * what you already have says nothing about what to do next, which is most of what a badge
   * is for. The shop is shown the same way: everything in it, on your own face, priced.
   */
  import {
    ACCESSORIES,
    type Accessory,
    AVATAR_MAX_BYTES,
    AVATAR_SIDE,
    COINS_PER_REPORT,
    FRAMES,
    type Frame,
    PAID_CELL_WINDOW_MS,
    PAID_REPORTS_PER_DAY,
  } from '$domain/looks.ts';
  import { request } from './api.ts';
  import Avatar from './Avatar.svelte';
  import { looks } from './looks.svelte.ts';
  import { describeError, session } from './session.svelte.ts';

  type Badge = { key: string; emoji: string; label: string; hint: string; earned: boolean };
  type Friend = {
    id: string;
    username: string;
    avatarEmoji: string;
    state: 'friends' | 'sent' | 'received';
  };
  type Wallet = {
    balance: number;
    owned: Frame[];
    wearing: Frame | null;
    accessories: Accessory[];
    accessory: Accessory | null;
  };

  /** The shop's two shelves: where to buy, where to put on, what the body names it. */
  const SHELVES = {
    frame: { buy: '/api/me/frames/', wear: '/api/me/frame', field: 'frame' },
    accessory: { buy: '/api/me/accessories/', wear: '/api/me/accessory', field: 'accessory' },
  } as const;
  type Shelf = keyof typeof SHELVES;

  type Props = { onclose: () => void; onwrapped: () => void };
  const { onclose, onwrapped }: Props = $props();

  /** "Oktober": the month Hotspotter Wrapped opens on (amendment 26). */
  const thisMonth = new Intl.DateTimeFormat('de-DE', { month: 'long' }).format(new Date());

  let badges = $state<Badge[]>([]);
  let friends = $state<Friend[]>([]);
  let wallet = $state<Wallet | undefined>();
  let name = $state('');
  let busy = $state(false);
  let error = $state('');
  let notice = $state('');
  /** What the last change to the picture or the shop came to, under the section it was in. */
  let said = $state<{ place: 'picture' | Shelf; text: string; error: boolean } | undefined>();
  /** The frame or accessory whose price was tapped once: the second tap buys it. */
  let asking = $state<string | undefined>();

  const username = $derived(session.user?.username ?? '');
  const emoji = $derived(session.user?.avatarEmoji ?? '🙂');
  const hasPicture = $derived(username !== '' && (looks.of(username)?.avatar ?? null) !== null);
  const earned = $derived(badges.filter((badge) => badge.earned));

  const CELL_HOURS = PAID_CELL_WINDOW_MS / 3_600_000;

  const STATE_LABELS: Record<Friend['state'], string> = {
    friends: 'befreundet',
    sent: 'angefragt',
    received: 'möchte dich hinzufügen',
  };

  async function load() {
    try {
      badges = (await request<{ badges: Badge[] }>('/api/badges')).badges;
      friends = (await request<{ friends: Friend[] }>('/api/friends')).friends;
      wallet = await request<Wallet>('/api/me/wallet');
      error = '';
    } catch (cause) {
      error = describeError(cause);
    }
  }

  $effect(() => {
    void load();
  });

  async function add(username: string) {
    busy = true;
    error = '';
    notice = '';
    try {
      const result = await request<{ result: string }>('/api/friends', {
        method: 'POST',
        body: JSON.stringify({ username }),
      });
      notice =
        result.result === 'accepted'
          ? `Ihr seid jetzt befreundet.`
          : result.result === 'already'
            ? 'Das steht schon.'
            : 'Anfrage geschickt.';
      name = '';
      await load();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  async function remove(id: string) {
    busy = true;
    try {
      await request(`/api/friends/${id}`, { method: 'DELETE' });
      await load();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  /** One change to the picture or from the shop, its outcome said under its own section. */
  async function change(place: 'picture' | Shelf, work: () => Promise<string | undefined>) {
    busy = true;
    said = undefined;
    try {
      const text = await work();
      if (text !== undefined) said = { place, text, error: false };
    } catch (cause) {
      said = { place, text: describeError(cause), error: true };
    } finally {
      busy = false;
    }
  }

  /**
   * A photo as the server takes it: the middle square, `AVATAR_SIDE` a side, as a JPEG. Made
   * here so that a twelve-megapixel photo never leaves the phone; what metadata the canvas
   * writes, the server strips again.
   */
  async function squareJpeg(file: File): Promise<Blob> {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_SIDE;
    canvas.height = AVATAR_SIDE;
    const context = canvas.getContext('2d');
    if (context === null) throw new Error('no canvas');
    // A JPEG has no transparency: what a PNG leaves see-through turns white rather than black.
    context.fillStyle = '#fff';
    context.fillRect(0, 0, AVATAR_SIDE, AVATAR_SIDE);
    const left = (bitmap.width - side) / 2;
    const top = (bitmap.height - side) / 2;
    context.drawImage(bitmap, left, top, side, side, 0, 0, AVATAR_SIDE, AVATAR_SIDE);
    bitmap.close();
    for (const quality of [0.85, 0.7, 0.5]) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      );
      if (blob !== null && blob.size <= AVATAR_MAX_BYTES) return blob;
    }
    throw new Error('too large');
  }

  async function choose(event: Event & { currentTarget: HTMLInputElement }) {
    const file = event.currentTarget.files?.[0];
    // The same photo chosen again is a choice too.
    event.currentTarget.value = '';
    if (file === undefined) return;
    await change('picture', async () => {
      let picture: Blob;
      try {
        picture = await squareJpeg(file);
      } catch {
        throw new Error('Dieses Bild kann die App nicht lesen. Ein JPEG oder PNG geht immer.');
      }
      const { avatar } = await request<{ avatar: number }>('/api/me/avatar', {
        method: 'PUT',
        body: picture,
        headers: { 'content-type': 'image/jpeg' },
      });
      looks.remember(username, { avatar });
      return 'Dein Bild ist drin.';
    });
  }

  async function removePicture() {
    await change('picture', async () => {
      await request('/api/me/avatar', { method: 'DELETE' });
      looks.remember(username, { avatar: null });
      return undefined;
    });
  }

  /** What the wallet says this device now looks like, without waiting for a read. */
  function dressed(next: Wallet) {
    wallet = next;
    looks.remember(username, { frame: next.wearing, accessory: next.accessory });
  }

  async function buy(shelf: Shelf, key: string) {
    if (asking !== key) {
      asking = key;
      return;
    }
    asking = undefined;
    await change(shelf, async () => {
      const bought = await request<{ wallet: Wallet }>(`${SHELVES[shelf].buy}${key}`, {
        method: 'POST',
      });
      dressed(bought.wallet);
      return 'Gekauft und angelegt.';
    });
  }

  async function wear(shelf: Shelf, key: string | null) {
    asking = undefined;
    await change(shelf, async () => {
      const worn = await request<{ wallet: Wallet }>(SHELVES[shelf].wear, {
        method: 'PUT',
        body: JSON.stringify({ [SHELVES[shelf].field]: key }),
      });
      dressed(worn.wallet);
      return undefined;
    });
  }
</script>

<section class="panel" aria-label="Profil">
  <header>
    <h2><Avatar {username} {emoji} size={40} /> @{username}</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <button class="story" onclick={onwrapped}>
    <strong>✨ Dein {thisMonth}</strong>
    <small>Hotspotter Wrapped – dein Monat als Story, zum Teilen</small>
  </button>

  <h3>Bild</h3>
  <div class="picture">
    <label class="button" class:disabled={busy}>
      {hasPicture ? 'Anderes Bild' : 'Bild wählen'}
      <input type="file" accept="image/*" disabled={busy} onchange={choose} />
    </label>
    {#if hasPicture}
      <button class="quiet" disabled={busy} onclick={removePicture}>Entfernen</button>
    {/if}
  </div>
  {#if said?.place === 'picture'}
    <p class="hint" class:error={said.error} role={said.error ? 'alert' : 'status'}>{said.text}</p>
  {/if}
  <p class="hint">
    Wird quadratisch zugeschnitten. Wo es aufgenommen wurde und womit, bleibt auf deinem Handy;
    sehen können es nur freigeschaltete Mitglieder.
  </p>

  <h3>Coins und Rahmen</h3>
  <p class="balance">🪙 <strong>{wallet?.balance ?? '–'}</strong> Hotspotter-Coins</p>
  <p class="hint">
    Jede Meldung bringt {COINS_PER_REPORT} 🪙 – bis zu {PAID_REPORTS_PER_DAY} am Tag, und dieselbe
    Wabe einmal in {CELL_HOURS} Stunden.
  </p>
  <!-- One thing on a shelf, tried on your own face: with the frame and accessory you wear,
       except for the one this tile sells. -->
  {#snippet ware(shelf: Shelf, key: string, label: string, price: number)}
    {@const owned =
      shelf === 'frame'
        ? (wallet?.owned.includes(key as Frame) ?? false)
        : (wallet?.accessories.includes(key as Accessory) ?? false)}
    {@const worn = (shelf === 'frame' ? wallet?.wearing : wallet?.accessory) === key}
    {@const missing = wallet === undefined ? price : price - wallet.balance}
    <li class:worn>
      {#if shelf === 'frame'}
        <Avatar {username} {emoji} size={44} frame={key as Frame} />
      {:else}
        <Avatar {username} {emoji} size={44} accessory={key as Accessory} />
      {/if}
      <span class="name">{label}</span>
      {#if worn}
        <button class="quiet" disabled={busy} onclick={() => wear(shelf, null)}>Abnehmen</button>
      {:else if owned}
        <button disabled={busy} onclick={() => wear(shelf, key)}>Anlegen</button>
      {:else if asking === key}
        <button class="buy" disabled={busy} onclick={() => buy(shelf, key)}>Kaufen?</button>
      {:else}
        <button
          disabled={busy || missing > 0}
          title={missing > 0 ? `Dir fehlen noch ${missing} 🪙` : undefined}
          onclick={() => buy(shelf, key)}
        >
          {price} 🪙
        </button>
      {/if}
    </li>
  {/snippet}

  <ul class="frames">
    {#each FRAMES as frame (frame.key)}
      {@render ware('frame', frame.key, frame.name, frame.price)}
    {/each}
  </ul>
  {#if said?.place === 'frame'}
    <p class="hint" class:error={said.error} role={said.error ? 'alert' : 'status'}>{said.text}</p>
  {/if}

  <h3>Accessoires</h3>
  <ul class="frames dress">
    {#each ACCESSORIES as accessory (accessory.key)}
      {@render ware('accessory', accessory.key, accessory.name, accessory.price)}
    {/each}
  </ul>
  {#if said?.place === 'accessory'}
    <p class="hint" class:error={said.error} role={said.error ? 'alert' : 'status'}>{said.text}</p>
  {/if}

  <h3>Abzeichen ({earned.length}/{badges.length})</h3>
  <ul class="badges">
    {#each badges as badge (badge.key)}
      <li class:earned={badge.earned} title={badge.hint}>
        <span class="emoji">{badge.emoji}</span>
        <span class="label">{badge.label}</span>
        {#if !badge.earned}<span class="hint">{badge.hint}</span>{/if}
      </li>
    {/each}
  </ul>

  <h3>Freunde</h3>
  <div class="add">
    <input
      bind:value={name}
      placeholder="Name"
      autocapitalize="none"
      spellcheck="false"
      maxlength="20"
      aria-label="Freund hinzufügen"
    />
    <button disabled={busy || name.trim() === ''} onclick={() => add(name.trim())}>
      Hinzufügen
    </button>
  </div>

  {#if notice}<p class="hint">{notice}</p>{/if}
  {#if error}<p class="hint error" role="alert">{error}</p>{/if}

  {#if friends.length === 0}
    <p class="hint">Noch niemand. Ein Name genügt.</p>
  {:else}
    <ul class="friends">
      {#each friends as friend (friend.id)}
        <li>
          <span>
            <Avatar username={friend.username} emoji={friend.avatarEmoji} /> @{friend.username}
          </span>
          <span class="state">{STATE_LABELS[friend.state]}</span>
          {#if friend.state === 'received'}
            <button disabled={busy} onclick={() => add(friend.username)}>Annehmen</button>
          {:else}
            <button class="quiet" disabled={busy} onclick={() => remove(friend.id)}>
              Entfernen
            </button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .panel {
    position: absolute;
    inset: max(3.5rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom));
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 30rem;
    margin-inline: auto;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 1.1rem;
  }

  h3 {
    margin: 0.5rem 0 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .story {
    display: grid;
    gap: 0.15rem;
    padding: 0.7rem 0.9rem;
    border: 0;
    border-radius: 0.75rem;
    background: linear-gradient(120deg, #2b1055, #a4206a 60%, #c2410c);
    color: #fff;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .story small {
    font-size: 0.75rem;
    opacity: 0.85;
  }

  .picture {
    display: flex;
    gap: 0.4rem;
  }

  /* The label is the button; the input it carries stays reachable by keyboard, just unseen. */
  .picture input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .picture .button:focus-within {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .picture .button.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .balance {
    margin: 0;
    font-size: 0.95rem;
  }

  .frames {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(5.75rem, 1fr));
    gap: 0.4rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .frames li {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    padding: 0.6rem 0.4rem 0.5rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    font-size: 0.75rem;
    text-align: center;
  }

  .frames li.worn {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 10%, var(--card));
  }

  /* Hats stand up off the head: room for them above the face. */
  .frames.dress li {
    padding-top: 1.3rem;
  }

  .frames .name {
    font-weight: 600;
  }

  .frames button {
    width: 100%;
  }

  .frames .buy {
    border-color: var(--accent);
    color: var(--accent);
    font-weight: 600;
  }

  .badges {
    display: grid;
    gap: 0.35rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .badges li {
    display: grid;
    grid-template-columns: 1.75rem auto;
    align-items: baseline;
    gap: 0.4rem;
    opacity: 0.4;
    font-size: 0.875rem;
  }

  .badges li.earned {
    opacity: 1;
  }

  .emoji {
    font-size: 1.15rem;
  }

  .badges .hint {
    grid-column: 2;
  }

  .add {
    display: flex;
    gap: 0.4rem;
  }

  .add input {
    flex: 1;
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
  }

  .add button,
  .friends button,
  .picture button,
  .picture .button,
  .frames button {
    padding: 0.4rem 0.7rem;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .frames button:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .friends {
    display: grid;
    gap: 0.3rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .friends li {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
  }

  .state,
  .hint {
    color: var(--muted);
    font-size: 0.75rem;
  }

  .hint {
    margin: 0;
  }

  .error {
    color: var(--accent);
  }

  .quiet {
    opacity: 0.7;
  }
</style>
